import React, { useState } from "react";
import { Swords, Loader2, Trophy, Sparkles, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import api from "../lib/apiClient";

function ScoreRing({ score, highlight }) {
  const R = 20, C = 2 * Math.PI * R;
  const off = C - (Math.min(100, score) / 100) * C;
  return (
    <div className="relative" style={{ width: 56, height: 56 }}>
      <svg width="56" height="56" className="-rotate-90">
        <circle cx="28" cy="28" r={R} strokeWidth="6" fill="none" className="text-slate-800" stroke="currentColor" />
        <circle cx="28" cy="28" r={R} strokeWidth="6" fill="none" strokeLinecap="round" stroke="currentColor"
          className={highlight ? "text-orange-400" : "text-teal-400"} strokeDasharray={C} strokeDashoffset={off}
          style={{ transition: "stroke-dashoffset 0.7s ease" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center font-display text-sm font-bold">{score}</div>
    </div>
  );
}

export default function ArenaPanel({ plan, value, onChange, readOnly }) {
  const [loading, setLoading] = useState(false);
  const arena = value;

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/plans/${plan.plan_id}/arena`);
      onChange && onChange(res.data);
      toast.success(`Winner: ${res.data.winner}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Arena run failed");
    } finally {
      setLoading(false);
    }
  };

  const fr = arena?.final_report;

  return (
    <div data-testid="arena-panel" className="space-y-5">
      <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Swords className="h-5 w-5 text-orange-400" />
            <h3 className="font-display text-lg font-bold">AI Model Arena</h3>
          </div>
          {!readOnly && (
            <Button data-testid="arena-run-button" onClick={run} disabled={loading} className="cta-btn">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Judging… (~1–2 min)</> : <><Sparkles className="h-4 w-4 mr-1" /> {arena ? "Re-run arena" : "Run the arena"}</>}
            </Button>
          )}
        </div>
        <p className="text-sm text-slate-400 mt-2">Several AI engines are working on your idea. An impartial AI judge scores them, crowns a winner, and the rest assist on the final report.</p>
      </div>

      {arena && (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            {arena.reports.map((r) => {
              const isWinner = r.model === arena.winner;
              return (
                <div key={r.model} data-testid={`arena-report-${r.model}`} className={`rounded-2xl border p-5 ${isWinner ? "border-orange-500/50 bg-orange-500/5" : "border-slate-800 bg-slate-900/50"}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold text-sm">{r.model}</div>
                    {isWinner && <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/40"><Trophy className="h-3 w-3 mr-1" /> Winner</Badge>}
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <ScoreRing score={arena.scores?.[r.model] ?? r.viability_score} highlight={isWinner} />
                    <div className="text-xs text-slate-400">
                      <div>Judge score</div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-200 mt-3 italic">"{r.verdict}"</p>
                  <div className="mt-3 space-y-1">
                    {(r.top_risks || []).slice(0, 2).map((x, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-xs text-slate-400"><AlertTriangle className="h-3 w-3 text-rose-400 mt-0.5 shrink-0" />{x}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {arena.rationale && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 text-sm text-slate-300">
              <span className="text-orange-300 font-semibold">Why {arena.winner} won: </span>{arena.rationale}
            </div>
          )}

          {fr && (
            <div data-testid="arena-final-report" className="rounded-2xl border border-teal-500/30 bg-teal-500/5 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="h-5 w-5 text-teal-300" />
                <h4 className="font-display text-lg font-bold">Final consensus report</h4>
                {typeof fr.viability_score === "number" && <Badge className="bg-teal-500/20 text-teal-200 ml-auto">Viability {fr.viability_score}/100</Badge>}
              </div>
              {fr.verdict && <p className="font-display text-base text-white">{fr.verdict}</p>}
              {fr.summary && <p className="text-sm text-slate-300 mt-2 leading-relaxed">{fr.summary}</p>}
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <div className="text-xs uppercase text-emerald-400 mb-1">Key strengths</div>
                  {(fr.key_strengths || []).map((x, i) => <div key={i} className="flex items-start gap-1.5 text-sm text-slate-200"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />{x}</div>)}
                </div>
                <div>
                  <div className="text-xs uppercase text-rose-400 mb-1">Key risks</div>
                  {(fr.key_risks || []).map((x, i) => <div key={i} className="flex items-start gap-1.5 text-sm text-slate-200"><AlertTriangle className="h-3.5 w-3.5 text-rose-400 mt-0.5 shrink-0" />{x}</div>)}
                </div>
              </div>
              {(fr.recommended_actions || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase text-sky-400 mb-1">Recommended actions</div>
                  {(fr.recommended_actions || []).map((x, i) => <div key={i} className="flex items-start gap-1.5 text-sm text-slate-200"><TrendingUp className="h-3.5 w-3.5 text-sky-400 mt-0.5 shrink-0" />{x}</div>)}
                </div>
              )}
              {fr.contributors && <p className="text-xs text-slate-500 mt-4 italic">{fr.contributors}</p>}
            </div>
          )}
        </>
      )}

      {!arena && !loading && (
        <p className="text-sm text-slate-500 italic text-center py-6">No arena yet. Run it to see the models compete.</p>
      )}
    </div>
  );
}
